import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { lazy, Suspense, useState } from "react";
import { Menu, X, Crown, LogOut, User, Sparkles, Gift, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import DisclaimerBanner from "@/components/DisclaimerBanner";
import { useAuth } from "@/context/AuthContext";
import { PremiumSources } from "@/components/PremiumSources";

const DeleteAccountDialog = lazy(() => import("@/components/DeleteAccountDialog").then((m) => ({ default: m.DeleteAccountDialog })));

const NAV = [
  { to: "/today", label: "Today" },
  { to: "/planner", label: "Planner & Dosage" },
  { to: "/schedule", label: "Daily Schedule" },
  { to: "/learn", label: "Learn" },
  { to: "/blog", label: "Blog" },
  { to: "/reminders", label: "Reminders" },
  { to: "/saved", label: "Saved Plans" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const [delOpen, setDelOpenRaw] = useState(false);
  // Lazy-load the dialog on first open, then keep it mounted so Radix can clean up body pointer-events on close.
  const [delLoaded, setDelLoaded] = useState(false);
  const setDelOpen = (v) => { if (v) setDelLoaded(true); setDelOpenRaw(v); };
  const loc = useLocation();
  const nav = useNavigate();
  const isHome = loc.pathname === "/";
  const { user, isPremium, logout } = useAuth();

  const doLogout = async () => { await logout(); nav("/"); };


  return (
    <div className="min-h-screen pv-grain">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0c1311]/85 border-b border-emerald-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 group">
            <img src="/lumivia-logo-96.webp" width="36" height="36" alt="Lumivia logo" className="h-9 w-9 rounded-xl object-cover ring-1 ring-emerald-800/50 transition-transform group-hover:scale-105" />
            <span className="font-head text-xl font-bold tracking-tight text-emerald-50">
              Lumi<span className="text-teal-400">via</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`}
                className={({ isActive }) =>
                  `px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive ? "bg-teal-400/15 text-teal-300" : "text-stone-300 hover:text-teal-200 hover:bg-white/5"
                  }`}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {!isPremium && (
              <Link to="/pricing">
                <Button data-testid="header-upgrade-btn" variant="outline" className="rounded-full border-teal-500/50 bg-teal-400/10 text-teal-200 hover:bg-teal-400/20">
                  <Sparkles className="h-4 w-4 mr-1.5" /> Upgrade
                </Button>
              </Link>
            )}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="account-menu-trigger" className="flex items-center gap-2 rounded-full border border-emerald-800/50 bg-[#141f1c] pl-1 pr-3 py-1 hover:border-teal-600">
                    <span className="h-7 w-7 rounded-full bg-teal-400/20 flex items-center justify-center text-teal-300"><User className="h-4 w-4" /></span>
                    <span className="text-sm text-emerald-50 max-w-[120px] truncate">{user.name || user.email}</span>
                    {isPremium && <Crown className="h-3.5 w-3.5 text-amber-400" />}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#141f1c] border-emerald-800/50 text-emerald-50 w-56">
                  <DropdownMenuLabel className="text-stone-400 font-normal text-xs truncate">{user.email}</DropdownMenuLabel>
                  <DropdownMenuItem className="cursor-default focus:bg-transparent">
                    <Badge className={isPremium ? "bg-amber-400/15 text-amber-300 border-0" : "bg-stone-700/40 text-stone-300 border-0"}>
                      {isPremium ? "Premium" : "Free plan"}
                    </Badge>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-emerald-900/50" />
                  <DropdownMenuItem data-testid="menu-referrals" onClick={() => nav("/referrals")} className="focus:bg-white/5">
                    <Gift className="h-4 w-4 mr-2 text-teal-300" /> Invite friends
                  </DropdownMenuItem>
                  {!isPremium && (
                    <DropdownMenuItem data-testid="menu-upgrade" onClick={() => nav("/pricing")} className="focus:bg-teal-400/10 text-teal-300">
                      <Sparkles className="h-4 w-4 mr-2" /> Upgrade to Premium
                    </DropdownMenuItem>
                  )}
                  {user.premium_sources?.length > 0 && (
                    <>
                      <DropdownMenuSeparator className="bg-emerald-900/50" />
                      <PremiumSources sources={user.premium_sources} className="px-2 py-1" />
                      <DropdownMenuSeparator className="bg-emerald-900/50" />
                    </>
                  )}
                  <DropdownMenuItem data-testid="menu-logout" onClick={doLogout} className="focus:bg-white/5">
                    <LogOut className="h-4 w-4 mr-2" /> Log out
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-delete-account" onSelect={() => setDelOpen(true)} className="focus:bg-red-500/10 text-red-400">
                    <Trash2 className="h-4 w-4 mr-2" /> Delete account
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login">
                <Button data-testid="header-login-btn" className="rounded-full bg-teal-400 hover:bg-teal-300 text-[#0c1311] font-semibold">
                  Log in
                </Button>
              </Link>
            )}
          </div>

          <button data-testid="mobile-menu-toggle" className="lg:hidden text-stone-200" onClick={() => setOpen((o) => !o)}>
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden bg-[#141f1c] border-t border-emerald-900/40 px-4 py-3 space-y-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.to.slice(1)}`}
                className={({ isActive }) => `block px-3 py-2.5 rounded-lg text-sm font-medium ${isActive ? "bg-teal-400/15 text-teal-300" : "text-stone-300"}`}>
                {n.label}
              </NavLink>
            ))}
            <div className="pt-2 flex flex-col gap-2">
              {!isPremium && <Link to="/pricing" onClick={() => setOpen(false)}><Button variant="outline" className="w-full rounded-full border-teal-500/50 bg-teal-400/10 text-teal-200"><Sparkles className="h-4 w-4 mr-1.5" /> Upgrade</Button></Link>}
              {user ? (
                <>
                  {user.premium_sources?.length > 0 && (
                    <PremiumSources sources={user.premium_sources} className="rounded-xl border border-emerald-800/40 bg-[#141f1c] px-4 py-2" />
                  )}
                  <Button onClick={() => { setOpen(false); doLogout(); }} className="w-full rounded-full bg-stone-700 text-stone-100 min-w-0"><LogOut className="h-4 w-4 mr-1.5 shrink-0" /><span className="truncate">Log out ({user.email})</span></Button>
                  <Button data-testid="mobile-delete-account" variant="ghost" onClick={() => { setOpen(false); setDelOpen(true); }} className="w-full rounded-full text-red-400 hover:bg-red-500/10 hover:text-red-300"><Trash2 className="h-4 w-4 mr-1.5" /> Delete account</Button>
                </>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)}><Button className="w-full rounded-full bg-teal-400 text-[#0c1311] font-semibold">Log in</Button></Link>
              )}
            </div>
          </div>
        )}
      </header>
      {user && delLoaded && <Suspense fallback={null}><DeleteAccountDialog open={delOpen} onOpenChange={setDelOpen} /></Suspense>}

      <main className={isHome ? "" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"}>{children}</main>

      <footer className="border-t border-emerald-900/40 mt-16 py-8 text-center text-xs text-stone-500 px-4">
        <p className="max-w-2xl mx-auto">
          Lumivia is an educational wellness tool. It is not a substitute for professional medical care.
          Always consult a qualified healthcare provider before starting any cleanse.
        </p>
        <div className="flex items-center justify-center gap-4 mt-3">
          <Link to="/privacy" data-testid="footer-privacy" className="hover:text-teal-300">Privacy</Link>
          <span className="text-stone-700">·</span>
          <Link to="/terms" data-testid="footer-terms" className="hover:text-teal-300">Terms</Link>
          <span className="text-stone-700">·</span>
          <Link to="/blog" data-testid="footer-blog" className="hover:text-teal-300">Blog</Link>
          <span className="text-stone-700">·</span>
          <Link to="/refunds" data-testid="footer-refunds" className="hover:text-teal-300">Refunds</Link>
          <span className="text-stone-700">·</span>
          <Link to="/delete-account" data-testid="footer-delete-account" className="hover:text-teal-300">Delete account</Link>
        </div>
        <p className="mt-3 font-mono">© {new Date().getFullYear()} Lumivia</p>
      </footer>
    </div>
  );
}
